import { Button, Modal, Text, Title } from '@bancobarigui/barihaus';
import { Upload, message } from 'antd';
import type { UploadFile } from 'antd';
import { UploadOutlined, FileImageOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useState } from 'react';

interface SignatureUploadProps {
  onUpload: (base64: string) => void;
  onRemove: () => void;
  loading: boolean;
}

export function SignatureUpload({ onUpload, onRemove, loading }: SignatureUploadProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [instructionModalOpen, setInstructionModalOpen] = useState(false);

  const handleUpload = async (file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      message.error('Formato inválido. Use apenas PNG ou JPG.');
      return false;
    }

    try {
      const base64 = await convertToBase64(file);
      onUpload(base64);
      return false;
    } catch {
      message.error('Erro ao carregar assinatura');
      return false;
    }
  };

  const handleRemove = () => {
    setFileList([]);
    onRemove();
  };

  const handleOpenInstructions = () => {
    setInstructionModalOpen(true);
  };

  const handleCloseInstructions = () => {
    setInstructionModalOpen(false);
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Upload
          beforeUpload={handleUpload}
          fileList={fileList}
          onRemove={handleRemove}
          accept="image/png,image/jpeg,image/jpg"
          maxCount={1}
        >
          <Button icon={<UploadOutlined />} loading={loading}>
            Upload de assinatura
          </Button>
        </Upload>
        <Button
          type="link"
          onClick={handleOpenInstructions}
          style={{ padding: 0 }}
        >
          Ver instruções
        </Button>
      </div>

      <Modal
        title="Instruções para Upload de Assinatura"
        open={instructionModalOpen}
        onCancel={handleCloseInstructions}
        footer={[
          <Button key="ok" type="primary" onClick={handleCloseInstructions}>
            Entendi
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              <FileImageOutlined style={{ marginRight: 8 }} />
              Formatos Aceitos
            </Title>
            <div style={{ display: 'flex', gap: 16, marginLeft: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text>PNG</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text>JPG / JPEG</Text>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>Recomendações</Title>
            <ul style={{ margin: 0, paddingLeft: 24, color: '#595959' }}>
              <li style={{ marginBottom: 8 }}>
                <Text>Use uma imagem com fundo branco ou transparente (PNG)</Text>
              </li>
              <li style={{ marginBottom: 8 }}>
                <Text>A assinatura deve estar legível e em boa resolução</Text>
              </li>
              <li style={{ marginBottom: 8 }}>
                <Text>Tamanho recomendado: entre 200x100 e 600x300 pixels</Text>
              </li>
              <li style={{ marginBottom: 8 }}>
                <Text>Evite imagens muito grandes (máximo 2MB)</Text>
              </li>
            </ul>
          </div>

          <div style={{
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
            padding: 12
          }}>
            <Title level={5} style={{ marginBottom: 4, color: '#d46b08' }}>
              Dica
            </Title>
            <Text style={{ color: '#ad6800' }}>
              Para melhor qualidade no documento final, prefira o formato PNG com fundo transparente.
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
}

async function convertToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
